import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { AuthContext, type AuthStatus } from "@/auth/authContext";
import {
  isWithinGrace,
  lastVerifiedAt as readLastVerifiedAt,
  noteTokenMinted,
} from "@/auth/offlineSession";
import { SignInDialog } from "@/auth/SignInDialog";
import {
  auth,
  getFreshIdToken,
  signInWithEmailPassword,
  signInWithGoogle as fbSignInWithGoogle,
  signInWithMicrosoft as fbSignInWithMicrosoft,
  signOutUser,
  subscribeToAuth,
  toSessionUser,
  type SessionUser,
} from "@/lib/firebase";

// Benign sign-in outcomes (§9.2.2) — the user dismissing or double-triggering the popup is
// not an error worth a toast.
const SILENT_SIGNIN_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

// Map a Firebase error code to a friendly, non-leaky message (§9.2.2). Bad email/password is
// deliberately not distinguished ("user not found" vs "wrong password") to avoid confirming
// which accounts exist.
function signInErrorMessage(code: string | undefined): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-email":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "Incorrect email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait and try again.";
    case "auth/popup-blocked":
      return "Popup blocked — allow popups for this site and try again.";
    case "auth/account-exists-with-different-credential":
      return "That email is already registered with a different sign-in method.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return "Sign-in failed. Please try again.";
  }
}

// §9.1.3 Wires `onAuthStateChanged` once, near the top of the tree, so the whole studio
// reacts to sign-in/out without a reload. Nothing here blocks rendering — the editor mounts
// immediately and auth resolves asynchronously, only flipping premium/export affordances
// (and the ID token the library authenticates with). Session-only: never persisted into
// DeckDocument (Firebase keeps its own session in its own IndexedDB — §9.1.2).
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [idToken, setIdToken] = useState<string | null>(null);
  // §14.6.3 The offline half of the session: when the network last confirmed this identity,
  // kept in state (not read from storage on every render) so consumers re-render when it
  // moves. `null` until an identity resolves.
  const [verifiedAt, setVerifiedAt] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Monotonic guard so a slow token fetch from a superseded identity can't overwrite a
  // newer one (rapid sign-in/out).
  const tokenSeq = useRef(0);
  // §9.4.2 An action to resume once the user signs in through the dialog (e.g. the export
  // that was gated). Runs on success, cleared on cancel — never carried across prompts.
  const pendingActionRef = useRef<(() => void) | null>(null);
  const statusRef = useRef<AuthStatus>("loading");
  statusRef.current = status;

  useEffect(() => {
    return subscribeToAuth((fbUser) => {
      const seq = ++tokenSeq.current;
      setUser(toSessionUser(fbUser));
      setStatus(fbUser ? "in" : "out");
      if (!fbUser) {
        setIdToken(null);
        setVerifiedAt(null);
        return;
      }
      // Offline, this is the *cached* user Firebase resolved from its own IndexedDB, so show
      // what we last knew about it before any mint is attempted (§14.6.7).
      setVerifiedAt(readLastVerifiedAt(fbUser.uid));
      // Eagerly mint a token for the new identity so the library gets it on handshake.
      void getFreshIdToken().then((t) => {
        if (seq !== tokenSeq.current) return;
        setIdToken(t);
        // §14.6.4 A failed mint does NOT sign the user out. `getFreshIdToken` returns null
        // rather than clearing the session, and this is the place where the natural "fix" —
        // calling signOut when the token is null — would put a consultant at 38,000 feet
        // back on the sign-in dialog with no way through it. Do not add it.
        if (t) setVerifiedAt(noteTokenMinted(fbUser.uid, t));
      });
    });
  }, []);

  // §14.2.10/§14.6.5 Re-verify the moment a network appears, so the 48h window keeps moving
  // and the user rarely meets it. Cheap: Firebase answers from cache when the token is still
  // good, and `noteTokenMinted` ignores a cached token, so this only ever records a real mint.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      const seq = tokenSeq.current;
      void getFreshIdToken(true).then((t) => {
        if (seq !== tokenSeq.current || !t) return;
        setIdToken(t);
        setVerifiedAt(noteTokenMinted(uid, t));
      });
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  // §9.2.1/§9.2.2 Run a provider sign-in, swallowing benign cancels and toasting real
  // failures. Resolves `true` only when actually signed in, so callers can close their UI.
  const runSignIn = useCallback(
    async (fn: () => Promise<unknown>): Promise<boolean> => {
      try {
        await fn();
        return true;
      } catch (err) {
        const code = (err as { code?: string })?.code;
        if (!code || !SILENT_SIGNIN_CODES.has(code)) {
          toast.error(signInErrorMessage(code));
        }
        return false;
      }
    },
    [],
  );

  const signInWithGoogle = useCallback(
    () => runSignIn(fbSignInWithGoogle),
    [runSignIn],
  );
  const signInWithMicrosoft = useCallback(
    () => runSignIn(fbSignInWithMicrosoft),
    [runSignIn],
  );
  const signInWithEmail = useCallback(
    (email: string, password: string) =>
      runSignIn(() => signInWithEmailPassword(email, password)),
    [runSignIn],
  );

  const signOut = useCallback(async () => {
    await signOutUser();
  }, []);

  const promptSignIn = useCallback((onSignedIn?: () => void) => {
    // Already signed in → run the gated action straight away, no dialog (§9.4.2).
    if (statusRef.current === "in") {
      onSignedIn?.();
      return;
    }
    pendingActionRef.current = onSignedIn ?? null;
    setDialogOpen(true);
  }, []);

  // Fired by the dialog the moment a sign-in succeeds (before it closes), so the pending
  // action resumes with the user genuinely signed in.
  const handleSignedIn = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  // A dialog close that wasn't a successful sign-in (cancel / dismiss) abandons the gated
  // action, so a later unrelated sign-in can't trigger a stale export.
  const handleDialogOpenChange = useCallback((open: boolean) => {
    if (!open) pendingActionRef.current = null;
    setDialogOpen(open);
  }, []);

  // Reads the live user, so it's always fresh (§8.3.3); `idToken` state above is only the
  // eager copy for the initial push. Every mint that succeeds also moves the offline window
  // (§14.6.1) — the Library refreshes on its own schedule, and that traffic is the most
  // reliable evidence we have that the network is back.
  const getIdToken = useCallback(async (forceRefresh = false) => {
    const uid = auth.currentUser?.uid ?? null;
    const token = await getFreshIdToken(forceRefresh);
    if (token && uid) {
      setIdToken(token);
      setVerifiedAt(noteTokenMinted(uid, token));
    } else if (!token && uid) {
      // Signed in, no usable bearer: the offline state (§14.6.3). The session stands.
      setIdToken(null);
    }
    return token;
  }, []);

  // Evaluated on every render rather than inside the memo: the window closes with the passage
  // of time, not with a state change, and a memo keyed on state would hold it open forever.
  const withinOfflineGrace = isWithinGrace(user?.uid ?? null, Date.now());

  const value = useMemo(
    () => ({
      user,
      status,
      idToken,
      // Holding a bearer right now is what "fresh" means — not "signed in", which stays true
      // offline (§14.6.3).
      tokenFresh: idToken !== null,
      lastVerifiedAt: verifiedAt,
      withinOfflineGrace,
      signInWithGoogle,
      signInWithMicrosoft,
      signInWithEmail,
      signOut,
      promptSignIn,
      getIdToken,
    }),
    [
      user,
      status,
      idToken,
      verifiedAt,
      withinOfflineGrace,
      signInWithGoogle,
      signInWithMicrosoft,
      signInWithEmail,
      signOut,
      promptSignIn,
      getIdToken,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* One shared dialog for the whole app, driven by `promptSignIn` (§9.2). */}
      <SignInDialog
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
        onSignedIn={handleSignedIn}
        auth={value}
      />
    </AuthContext.Provider>
  );
}
